import * as Backend from "../backend/v1/sdk.gen";
import type { ActionLogEvent } from "../backend/v1/types.gen";
import {
    showAnimalDetails,
    showCageDetails,
    showDrugPackageDetails,
    showDrugRequestDetails,
    showEmbryoDetails,
    showLicenseDetails,
    showOrderRequestDetails,
    showProjectDetails,
    showPupDetails,
    showServiceRequestDetails,
    showSpermDetails,
    showStrainDetails,
    showTankDetails,
    showWorkRequestDetails,
} from "../dialogs";
import { mainMenu } from "../pyratFrontend";
import { translated } from "./localize";
import { baseUrl, getUrl, printElement } from "./utils";

// import required for Safari < 15.4 (released 2022-03, required until 2024-03)

export class ActionLog {
    private dialog: HTMLDialogElement;
    private container: HTMLDivElement;
    private lastRowDate: string;

    constructor() {
        this.dialog = document.createElement("dialog");
        this.dialog.classList.add("tw:m-2");
        this.dialog.classList.add("tw:p-0");
        this.dialog.classList.add("tw:min-w-[320px]");
        this.dialog.classList.add("tw:max-w-full");
        this.dialog.classList.add("tw:z-20");
        this.dialog.classList.add("tw:border-solid");
        this.dialog.classList.add("tw:rounded-sm");
        this.dialog.classList.add("tw:border-2");
        this.dialog.classList.add("tw:border-base-color-layout");
        this.dialog.classList.add("tw:me-auto");
        this.dialog.style.height = "calc(100vh - 1.25rem)";
        this.dialog.style.transitionProperty = "margin-left,width";
        this.dialog.style.transitionDuration = ".3s";

        this.container = document.createElement("div");
        this.container.classList.add("tw:flex");
        this.container.classList.add("tw:flex-col");
        this.container.classList.add("tw:h-full");
        this.container.classList.add("tw:m-0");
        this.container.classList.add("tw:p-0");
        this.dialog.append(this.container);

        window.addEventListener("load", () => {
            document.body.append(this.dialog);
        });
    }

    private closeOnClickHandler = (event: MouseEvent) => {
        if (!this.dialog.contains(event.target as Node)) {
            this.close();
        }
    };

    public open() {
        setTimeout(() => document.addEventListener("click", this.closeOnClickHandler), 0);
        this.dialog.style.marginLeft = "-100%";
        this.dialog.show();
        this.dialog.style.marginLeft = "";
        this.reload();
    }

    public close() {
        document.removeEventListener("click", this.closeOnClickHandler);
        this.dialog.style.marginLeft = "-100%";
        setTimeout(() => {
            this.dialog.close();
            this.dialog.style.marginLeft = "";
        }, 300);
    }

    public clearSelection() {
        Backend.clearGlobalSelection().then(() => this.reload());
    }

    public print() {
        printElement(this.container, { obtainStylesheet: true });
    }

    public export() {
        window.open(getUrl(baseUrl("/frontend/action_log/csv")), "_blank");
    }

    public reload() {
        this.container.innerHTML = "";

        const loadingElement = document.createElement("div");
        loadingElement.classList.add("loading_circle");
        this.container.append(loadingElement);

        Backend.getActionLog().then(({ data: actionLog }) => {
            this.container.innerHTML = "";
            this.lastRowDate = "";

            const header = document.createElement("header");
            this.container.append(header);
            header.classList.add("tw:gap-2");
            header.classList.add("tw:flex");
            header.classList.add("tw:justify-between");
            header.classList.add("tw:m-2");
            header.classList.add("tw:p-2");
            header.classList.add("tw:border");
            header.classList.add("tw:border-shade-color-82");
            header.classList.add("tw:shadow-xs");
            header.classList.add("tw:bg-shade-color-90");
            header.classList.add("tw:print:hidden");

            const leftButtons = document.createElement("div");
            leftButtons.classList.add("tw:gap-2");
            leftButtons.classList.add("tw:flex");
            header.append(leftButtons);

            const rightButtons = document.createElement("div");
            rightButtons.classList.add("tw:gap-2");
            rightButtons.classList.add("tw:flex");
            header.append(rightButtons);

            const closeButton = document.createElement("input");
            closeButton.type = "button";
            closeButton.value = translated("Close");
            closeButton.addEventListener("click", () => this.close());
            leftButtons.append(closeButton);

            const printButton = document.createElement("input");
            printButton.type = "button";
            printButton.value = translated("Print");
            printButton.addEventListener("click", () => this.print());
            rightButtons.append(printButton);

            const exportButton = document.createElement("input");
            exportButton.type = "button";
            exportButton.value = translated("Export");
            exportButton.addEventListener("click", () => this.export());
            rightButtons.append(exportButton);

            const eventContainer = document.createElement("div");
            eventContainer.classList.add("tw:overflow-y-auto");
            eventContainer.classList.add("tw:print:overflow-y-visible");
            eventContainer.classList.add("tw:px-4");
            eventContainer.classList.add("tw:mb-4");
            eventContainer.classList.add("tw:grid");
            eventContainer.classList.add("tw:gap-1");
            eventContainer.classList.add("tw:grid-cols-[min-content_min-content_auto]");

            if (
                actionLog.selection.animal_ids.length ||
                actionLog.selection.pup_ids.length ||
                actionLog.selection.cage_ids.length ||
                Object.keys(actionLog.selection.tanks).length
            ) {
                const clearButtton = document.createElement("input");
                clearButtton.type = "button";
                clearButtton.value = translated("Clear selection");
                clearButtton.addEventListener("click", () => this.clearSelection());
                header.append(clearButtton);
            }

            if (actionLog.event_list.length) {
                actionLog.event_list.forEach((event) => {
                    this.addEvent(eventContainer, event);
                });
            } else {
                const noEvents = document.createElement("p");
                noEvents.innerText = translated("The action log is empty.");
                noEvents.classList.add("tw:col-span-full");
                eventContainer.append(noEvents);
            }
            this.container.append(eventContainer);
        });
    }

    private addEvent(container: HTMLDivElement, event: ActionLogEvent) {
        const eventSpacer = document.createElement("div");
        eventSpacer.classList.add("tw:col-span-full");
        container.append(eventSpacer);

        // Event meta data

        if (event.formatted_event_date !== this.lastRowDate) {
            const newDateSpacer = document.createElement("div");
            newDateSpacer.classList.add("tw:col-span-full");
            container.append(newDateSpacer);
        }

        const date = document.createElement("div");
        if (event.formatted_event_date !== this.lastRowDate) {
            date.classList.add("tw:font-bold");
            date.classList.add("tw:text-basic-color-white");
            date.classList.add("tw:p-1");
            date.classList.add("tw:rounded-sm");
            date.classList.add("tw:w-min");
            date.classList.add("tw:bg-main-color-medium");
            date.classList.add("tw:no-underline");
            date.classList.add("tw:col-span-full");
            date.innerText = event.formatted_event_date;
            container.append(date);
        }

        const time = document.createElement("div");
        time.classList.add("tw:font-bold");
        time.classList.add("tw:text-main-color-medium");
        time.innerText = event.formatted_event_time;
        container.append(time);

        const title = document.createElement("div");
        title.classList.add("tw:col-span-2");
        title.classList.add("tw:font-bold");
        title.classList.add("tw:text-main-color-medium");
        title.innerText = event.title;
        container.append(title);

        this.lastRowDate = event.formatted_event_date;

        // Event relations

        const filterSymbol = document.createElement("span");
        filterSymbol.classList.add("tw:font-icon");
        filterSymbol.innerText = "≚";

        if (event.cages) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.cages.length > 1) {
                label.innerText = translated("Cages") + ` (${event.cages.length})`;
            } else {
                label.innerText = translated("Cage");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.cages.forEach((cage, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.innerText = cage.cage_number;
                link.addEventListener("click", () => {
                    showCageDetails({ cageId: cage.cage_id });
                    this.close();
                });
                value.append(link);
            });
            if (event.cages.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Cage list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.addEventListener("click", () =>
                mainMenu.openAndResetListFilter("get_cage_list", {
                    cageid: event.cages.map((c) => c.cage_id),
                }),
            );
            value.append(listLink);

            container.append(value);
        }

        if (event.new_cages?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.new_cages.length > 1) {
                label.innerText = translated("New cages") + ` (${event.new_cages.length})`;
            } else {
                label.innerText = translated("New cage");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.new_cages.forEach((cage, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.innerText = cage.cage_number;
                link.addEventListener("click", () => {
                    showCageDetails({ cageId: cage.cage_id });
                    this.close();
                });
                value.append(link);
            });
            if (event.new_cages.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Cage list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.addEventListener("click", () =>
                mainMenu.openAndResetListFilter("get_cage_list", {
                    cageid: event.new_cages.map((c) => c.cage_id),
                }),
            );
            value.append(listLink);

            container.append(value);
        }

        if (event.animals?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.animals.length > 1) {
                label.innerText = translated("Animals") + ` (${event.animals.length})`;
            } else {
                label.innerText = translated("Animal");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.animals.forEach((animal, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.innerText = animal.eartag;
                link.addEventListener("click", () => {
                    showAnimalDetails({ animalId: animal.animal_id });
                    this.close();
                });
                value.append(link);
            });
            if (event.animals.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Animal list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.addEventListener("click", () =>
                mainMenu.openAndResetListFilter("get_animal_list", {
                    animalid: event.animals.map((a) => a.animal_id),
                }),
            );
            value.append(listLink);

            container.append(value);
        }

        if (event.pups?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.pups.length > 1) {
                label.innerText = translated("Pups") + ` (${event.pups.length})`;
            } else {
                label.innerText = translated("Pup");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.pups.forEach((pup, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.innerText = pup.eartag;
                link.addEventListener("click", () => {
                    showPupDetails({ pupId: pup.pup_id });
                    this.close();
                });
                value.append(link);
            });
            if (event.pups.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Pup list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.addEventListener("click", () =>
                mainMenu.openAndResetListFilter("get_pup_list", {
                    pupid: event.pups.map((p) => p.pup_id),
                }),
            );
            value.append(listLink);

            container.append(value);
        }

        if (event.stud_males?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.stud_males.length > 1) {
                label.innerText = translated("Stud males") + ` (${event.stud_males.length})`;
            } else {
                label.innerText = translated("Stud male");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.stud_males.forEach((studMale, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.innerText = studMale.eartag;
                link.addEventListener("click", () => {
                    showAnimalDetails({ animalId: studMale.animal_id });
                    this.close();
                });
                value.append(link);
            });
            if (event.stud_males.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            container.append(value);
        }

        if (event.tanks?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap", "tw:col-start-2", "tw:pt-0.5");
            if (event.tanks.length > 1) {
                label.innerText = translated("Tanks") + ` (${event.tanks.length})`;
            } else {
                label.innerText = translated("Tank");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.tanks.forEach((tank, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:mx-0.5");
                link.innerText = `#${tank.tank_id}`;
                link.addEventListener("click", () => {
                    showTankDetails({ tankId: tank.tank_id });
                    this.close();
                });
                value.append(link);
            });
            if (event.tanks.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:m-0.5!");
            listLink.innerText = translated("Tank list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_tank_list"), {
                tank_id: event.tanks.map(({ tank_id }) => tank_id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.new_project) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            label.innerText = translated("Project");
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            const newProject = event.new_project;
            const link = document.createElement("a");
            link.classList.add("subtle-link");
            link.classList.add("tw:inline-block");
            link.classList.add("tw:whitespace-nowrap");
            link.classList.add("tw:mx-0.5");
            link.classList.add("cage-link");
            link.innerText = newProject.name;
            link.addEventListener("click", () => {
                showProjectDetails({
                    projectId: newProject.id,
                });
                this.close();
            });
            value.append(link);

            container.append(value);
        }

        if (event.work_requests?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.work_requests.length > 1) {
                label.innerText = translated("Work requests") + ` (${event.work_requests.length})`;
            } else {
                label.innerText = translated("Work request");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.work_requests.forEach((workRequest, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.classList.add("cage-link");
                link.innerText = `#${workRequest.work_request_id}`;
                link.addEventListener("click", () => {
                    showWorkRequestDetails({
                        workRequestId: workRequest.work_request_id,
                    });
                    this.close();
                });
                value.append(link);
            });
            if (event.work_requests.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Work request list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_work_request_list"), {
                id: event.work_requests.map((w) => w.work_request_id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.order_requests?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.order_requests.length > 1) {
                label.innerText = translated("Order requests") + ` (${event.order_requests.length})`;
            } else {
                label.innerText = translated("Order request");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.order_requests.forEach((orderRequest, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.classList.add("cage-link");
                link.innerText = `#${orderRequest.order_request_id}`;
                link.addEventListener("click", () => {
                    showOrderRequestDetails({
                        orderRequestId: orderRequest.order_request_id,
                    });
                    this.close();
                });
                value.append(link);
            });
            if (event.order_requests.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Order request list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_order_request_list"), {
                id: event.order_requests.map((o) => o.order_request_id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.service_requests?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.service_requests.length > 1) {
                label.innerText = translated("Service requests") + ` (${event.service_requests.length})`;
            } else {
                label.innerText = translated("Service request");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.service_requests.forEach((serviceRequest, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.classList.add("cage-link");
                link.innerText = `#${serviceRequest.service_request_id}`;
                link.addEventListener("click", () => {
                    showServiceRequestDetails({
                        serviceRequestId: serviceRequest.service_request_id,
                    });
                    this.close();
                });
                value.append(link);
            });
            if (event.service_requests.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Service request list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_service_request_list"), {
                id: event.service_requests.map((s) => s.service_request_id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.drug_requests?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.drug_requests.length > 1) {
                label.innerText = translated("Drug requests") + ` (${event.drug_requests.length})`;
            } else {
                label.innerText = translated("Drug request");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.drug_requests.forEach((drugRequest, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.classList.add("cage-link");
                link.innerText = `#${drugRequest.drug_request_id}`;
                link.addEventListener("click", () => {
                    showDrugRequestDetails({
                        drugRequestId: drugRequest.drug_request_id,
                    });
                    this.close();
                });
                value.append(link);
            });
            if (event.drug_requests.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Drug request list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_drug_request_list"), {
                id: event.drug_requests.map((d) => d.drug_request_id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.drug_packages?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap");
            label.classList.add("tw:col-start-2");
            label.classList.add("tw:pt-0.5");
            if (event.drug_packages.length > 1) {
                label.innerText = translated("Drug packages") + ` (${event.drug_packages.length})`;
            } else {
                label.innerText = translated("Drug package");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");
            event.drug_packages.forEach((drugPackage, index) => {
                if (index > 2) {
                    return;
                }
                const link = document.createElement("a");
                link.classList.add("subtle-link");
                link.classList.add("tw:inline-block");
                link.classList.add("tw:whitespace-nowrap");
                link.classList.add("tw:mx-0.5");
                link.classList.add("cage-link");
                link.innerText = `#${drugPackage.drug_package_id}`;
                link.addEventListener("click", () => {
                    showDrugPackageDetails({
                        drugPackageId: drugPackage.drug_package_id,
                    });
                    this.close();
                });
                value.append(link);
            });
            if (event.drug_packages.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link");
            listLink.classList.add("tw:inline-block");
            listLink.classList.add("tw:whitespace-nowrap");
            listLink.classList.add("tw:m-0.5!");
            listLink.innerText = translated("Drug package list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_drug_package_list"), {
                id: event.drug_packages.map((d) => d.drug_package_id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.sperms?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap", "tw:col-start-2", "tw:pt-0.5");
            if (event.sperms.length > 1) {
                label.innerText = translated("Sperm records") + ` (${event.sperms.length})`;
            } else {
                label.innerText = translated("Sperm record");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");

            event.sperms.forEach((sperm, index) => {
                if (index > 2) return;

                const link = document.createElement("a");
                link.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:mx-0.5");
                link.innerText = `#${sperm.id}`;
                link.addEventListener("click", () => {
                    showSpermDetails({ spermId: sperm.id });
                    this.close();
                });
                value.append(link);
            });

            if (event.sperms.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:m-0.5!");
            listLink.innerText = translated("Sperm list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_sperm_list"), {
                sperm_id: event.sperms.map(({ id }) => id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.strains?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap", "tw:col-start-2", "tw:pt-0.5");
            if (event.strains.length > 1) {
                label.innerText = translated("Lines / Strains") + ` (${event.strains.length})`;
            } else {
                label.innerText = translated("Line / Strain");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");

            event.strains.forEach((strain, index) => {
                if (index > 2) return;

                const link = document.createElement("a");
                link.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:mx-0.5");
                link.innerText = `${strain.name_with_id}`;
                link.addEventListener("click", () => {
                    showStrainDetails({
                        strainId: strain.id,
                    });
                    this.close();
                });
                value.append(link);
            });

            if (event.strains.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:m-0.5!");
            listLink.innerText = translated("Line / Strain list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_strain_list"), {
                id: event.strains.map(({ id }) => id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.embryo_groups?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap", "tw:col-start-2", "tw:pt-0.5");
            if (event.embryo_groups.length > 1) {
                label.innerText = translated("Embryo Groups") + ` (${event.embryo_groups.length})`;
            } else {
                label.innerText = translated("Embryo Group");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");

            event.embryo_groups.forEach((group, index) => {
                if (index > 2) return;

                const link = document.createElement("a");
                link.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:mx-0.5");
                link.innerText = `#${group.embryo_count}`;
                link.addEventListener("click", () => {
                    showEmbryoDetails({ embryoGroupKey: group.embryo_groupkey });
                    this.close();
                });
                value.append(link);
            });

            if (event.embryo_groups.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:m-0.5!");
            listLink.innerText = translated("Embryo list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_embryo_list"), {
                embryo_groupkey: event.embryo_groups.map(({ embryo_groupkey }) => embryo_groupkey).join(","),
            });
            value.append(listLink);

            container.append(value);
        }

        if (event.licenses?.length) {
            const label = document.createElement("div");
            label.classList.add("tw:whitespace-nowrap", "tw:col-start-2", "tw:pt-0.5");
            if (event.licenses.length > 1) {
                label.innerText = translated("Licenses") + ` (${event.licenses.length})`;
            } else {
                label.innerText = translated("License");
            }
            container.append(label);

            const value = document.createElement("div");
            value.classList.add("tw:max-w-[300px]");

            event.licenses.forEach((license, index) => {
                if (index > 2) return;

                const link = document.createElement("a");
                link.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:mx-0.5");
                link.innerText = `${license.license_number}`;
                link.addEventListener("click", () => {
                    showLicenseDetails({ licenseId: license.license_id });
                    this.close();
                });
                value.append(link);
            });

            if (event.licenses.length > 3) {
                const ellipsis = document.createElement("span");
                ellipsis.innerText = "...";
                ellipsis.classList.add("tw:mx-1");
                value.append(ellipsis);
            }

            const listLink = document.createElement("a");
            listLink.classList.add("subtle-link", "tw:inline-block", "tw:whitespace-nowrap", "tw:m-0.5!");
            listLink.innerText = translated("License list");
            listLink.prepend(filterSymbol.cloneNode(true));
            listLink.href = getUrl(baseUrl("/frontend/redirect/frontend:v1:get_license_list"), {
                license_id: event.licenses.map(({ license_id }) => license_id).join(","),
            });
            value.append(listLink);

            container.append(value);
        }
    }
}
